import {
  Box,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  Typography,
  Chip,
} from "@mui/material";
import { MutableRefObject } from "react";
import { useNavigate } from "react-router-dom";
import { useChatSessionsContext } from "../lib/contexts/ChatSessionsContext";
import { useCustomThemeContext } from "../lib/contexts/ThemeContext";
import { SessionSearchResult } from "../lib/interfaces/Chat";

interface SearchResultsProps {
  searchResults: SessionSearchResult[];
  searchQuery: string;
  searchLoading: boolean;
  isLoading: MutableRefObject<boolean>;
  windowSize: { width: number };
}

export default function SearchResults({
  searchResults,
  searchQuery,
  searchLoading,
  isLoading,
  windowSize,
}: SearchResultsProps) {
  const { custom } = useCustomThemeContext();
  const { updateSelectedChatSession, selectedChatSession, handleDrawerClose } =
    useChatSessionsContext();

  let navigate = useNavigate();

  const handleChatSessionClick = (chatId: string) => {
    isLoading.current = false;
    updateSelectedChatSession(chatId);
    handleDrawerClose();
    navigate(`/chat/${chatId}`);
  };

  // Helper function to safely parse and render highlighted text
  const renderHighlightedText = (text: string) => {
    if (!text) return text;

    // Split text by <mark> tags and render them with highlighting
    const parts = text.split(/(<mark>.*?<\/mark>)/g);
    return parts.map((part, index) => {
      if (part.startsWith("<mark>") && part.endsWith("</mark>")) {
        const highlightedText = part.replace(/<\/?mark>/g, "");
        return (
          <span
            key={index}
            style={{
              backgroundColor: custom.palette.primary.light + "40",
              color: custom.palette.primary.main,
              fontWeight: "bold",
            }}
          >
            {highlightedText}
          </span>
        );
      }
      return part;
    });
  };

  // Show loading message while searching
  if (searchLoading && searchQuery.trim()) {
    return (
      <Box sx={{ textAlign: "center", padding: 2 }}>
        <Typography
          variant="body2"
          sx={{
            color: custom.palette.primary.light,
            fontStyle: "italic",
          }}
        >
          Searching...
        </Typography>
      </Box>
    );
  }

  // Show no results message
  if (searchResults.length === 0 && !searchLoading && searchQuery.trim()) {
    return (
      <Box sx={{ textAlign: "center", padding: 2 }}>
        <Typography
          variant="body2"
          sx={{
            color: custom.palette.primary.light,
            fontStyle: "italic",
          }}
        >
          No sessions found for "{searchQuery}"
        </Typography>
      </Box>
    );
  }

  if (windowSize.width <= 10) {
    // Mobile view - simplified search results
    return (
      <List>
        {searchResults.map((result: SessionSearchResult) => (
          <ListItem key={result.chat_id} disablePadding>
            <ListItemButton
              onClick={() => handleChatSessionClick(result.chat_id)}
            >
              <ListItemText
                primary={renderHighlightedText(result.session_title)}
                secondary={result.preview_text}
                primaryTypographyProps={{
                  style: {
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                  },
                }}
                secondaryTypographyProps={{
                  style: {
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                    fontSize: "0.75rem",
                  },
                }}
              />
            </ListItemButton>
          </ListItem>
        ))}
      </List>
    );
  }

  // Desktop view - enhanced search results with highlights and relevance
  return (
    <List>
      <Box>
        <Typography
          variant="button"
          sx={{
            color: custom.palette.primary.light,
            fontWeight: "bold",
            letterSpacing: "0.08em",
            fontSize: "0.9rem",
            display: "block",
            marginBottom: 1,
          }}
        >
          Search Results ({searchResults.length})
        </Typography>
        {searchResults.map((result: SessionSearchResult) => (
          <ListItem
            key={result.chat_id}
            disablePadding
            sx={{ marginBottom: 1 }}
          >
            <ListItemButton
              selected={result.chat_id === selectedChatSession}
              onClick={() => handleChatSessionClick(result.chat_id)}
              sx={{
                flexDirection: "column",
                alignItems: "flex-start",
                padding: 1.5,
                border: `1px solid ${custom.palette.primary.light}20`,
                borderRadius: 1,
                "&:hover": {
                  border: `1px solid ${custom.palette.primary.light}60`,
                },
              }}
            >
              {/* Session Title with highlighting */}
              <Box sx={{ width: "100%", marginBottom: 0.5 }}>
                <Typography
                  variant="subtitle2"
                  sx={{
                    fontWeight: "bold",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                    width: "100%",
                  }}
                >
                  {renderHighlightedText(result.session_title)}
                </Typography>
              </Box>

              {/* Relevance Score and Exchange Count */}
              <Box sx={{ display: "flex", gap: 1, marginBottom: 0.5 }}>
                <Chip
                  label={`${(result.relevance_score * 100).toFixed(1)}% match`}
                  size="small"
                  variant="outlined"
                  sx={{
                    fontSize: "0.6rem",
                    height: "20px",
                    borderColor: custom.palette.primary.light,
                    color: custom.palette.primary.light,
                  }}
                />
                <Chip
                  label={`${result.exchange_count} exchanges`}
                  size="small"
                  variant="outlined"
                  sx={{
                    fontSize: "0.6rem",
                    height: "20px",
                    borderColor: custom.palette.primary.light,
                    color: custom.palette.primary.light,
                  }}
                />
              </Box>

              {/* Preview Text or Highlights */}
              {(result.preview_text || result.highlights) && (
                <Box sx={{ width: "100%" }}>
                  <Typography
                    variant="caption"
                    sx={{
                      color: custom.palette.text.secondary,
                      fontSize: "0.7rem",
                      lineHeight: 1.3,
                      display: "-webkit-box",
                      WebkitLineClamp: 2,
                      WebkitBoxOrient: "vertical",
                      overflow: "hidden",
                    }}
                  >
                    {result.highlights && result.highlights.length > 0
                      ? renderHighlightedText(result.highlights[0])
                      : result.preview_text}
                  </Typography>
                </Box>
              )}

              {/* Matched Exchanges Preview */}
              {result.matched_exchanges &&
                result.matched_exchanges.length > 0 && (
                  <Box sx={{ width: "100%", marginTop: 0.5 }}>
                    <Typography
                      variant="caption"
                      sx={{
                        color: custom.palette.primary.light,
                        fontSize: "0.65rem",
                        fontStyle: "italic",
                      }}
                    >
                      Relevant Q&A: {result.matched_exchanges.length} matches
                    </Typography>
                  </Box>
                )}

              {/* Date Information */}
              <Box sx={{ width: "100%", marginTop: 0.5 }}>
                <Typography
                  variant="caption"
                  sx={{
                    color: custom.palette.text.disabled,
                    fontSize: "0.6rem",
                  }}
                >
                  {new Date(result.updated_at).toLocaleDateString()}
                </Typography>
              </Box>
            </ListItemButton>
          </ListItem>
        ))}
      </Box>
    </List>
  );
}
